import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Chip, Stack, TableCell, TableRow } from '@mui/material';
import BasicTable, { NUMERIC_CELL_SX } from '../../../components/BasicTable/BasicTable';
import ListToolbar from '../../../components/ListToolbar/ListToolbar';
import RowActionsMenu from '../../../components/RowActionsMenu/RowActionsMenu';
import FilteredEmptyState from '../../../components/FilteredEmptyState';
import { useListUrlState } from '../../../utilities/hooks/useListUrlState';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import GenericInfoModal from '../../../components/GenericInfoModal';
import PlatformItemModal from './PlatformItemModal';
import { ReactComponent as AddIcon } from '../../../assets/add-icon.svg';
import { ITableHeadings } from '../../../common/interfaces/common.interface';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { ActionMode, GeneralStatusEnum } from '../../../common/enums/common.enum';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { formatMoney } from '../../../utilities/helper/money.helper';
import { BILLING_ITEM_TYPE_LABELS } from '../../../common/enums/invoicing.enum';
import { BillingItemDto } from '../../../common/interfaces/invoicing.interface';
import { deletePlatformBillingItem, getPlatformBillingItems } from '../../../api/invoicing.api';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
export const PLATFORM_ITEMS_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'common.name', sortable: false, width: '20%' },
  { id: 2, value: '', label: 'billing.lines.invoiceLabel', sortable: false, width: '24%' },
  { id: 3, value: '', label: 'billing.items.calculation', sortable: false },
  { id: 4, value: '', label: 'billing.items.unit', sortable: false },
  { id: 5, value: '', label: 'billing.items.netPrice', sortable: false, align: 'right' },
  { id: 6, value: '', label: 'billing.lines.vat', sortable: false },
  { id: 7, value: '', label: 'common.status', sortable: false },
  { id: 8, value: '', label: '', sortable: false, width: 56 }
];

/**
 * Catalogue defaults every kindergarten inherits.
 *
 * A kindergarten adjusts these locally; anything it leaves alone keeps following what
 * is set here, so a price change made on this screen reaches everyone who has not
 * overridden it.
 */
const PlatformItemsTab: React.FC = () => {
  const { t } = useTranslation();
  const list = useListUrlState();
  const { search, page, perPage } = list;
  const [items, setItems] = useState<BillingItemDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loader, setLoader] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BillingItemDto | null>(null);
  const [toDelete, setToDelete] = useState<BillingItemDto | null>(null);

  const loadItems = useCallback(async () => {
    setLoader(true);
    try {
      const result = await getPlatformBillingItems({
        page,
        perPage,
        search: search || undefined
      });
      setItems(result.results ?? []);
      setTotal(result.paginationInfo?.totalItems ?? 0);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('billing.platform.loadFailed'));
    } finally {
      setLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, search]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deletePlatformBillingItem(toDelete.id);
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.platform.removed'));
      setToDelete(null);
      loadItems();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('billing.items.removeFailed'));
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        {t('billing.platform.explanation')}
      </Alert>

      {/* the page title is in the frame above, so only the action stays on this row */}
      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mb: 2 }}>
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          startIcon={<AddIcon />}
          label={t('billing.items.create')}
          onClick={() => setCreating(true)}
        />
      </Stack>

      <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4 }}>
        <ListToolbar search={{ value: search, onChange: list.setSearch, placeholder: t('billing.items.searchNameOrLabel') }} />

        <BasicTable
          headings={PLATFORM_ITEMS_TABLE_HEADINGS}
          loader={loader}
          {...list.tableProps(total)}
          emptyState={search ? <FilteredEmptyState onClear={() => list.clearFilters([])} /> : undefined}
        >
          {items.map(item => (
            <TableRow
              key={item.id}
              hover
              tabIndex={0}
              sx={{ cursor: 'pointer' }}
              onClick={() => setEditing(item)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setEditing(item);
                }
              }}
            >
              <TableCell>{item.name}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{item.pdfLabel}</TableCell>
              <TableCell>{t(BILLING_ITEM_TYPE_LABELS[item.type])}</TableCell>
              <TableCell>{item.unit ?? '—'}</TableCell>
              <TableCell sx={NUMERIC_CELL_SX}>{formatMoney(item.unitPriceNet)}</TableCell>
              <TableCell>{item.vatRate}%</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={t(`enums.generalStatus.${item.status}`)}
                  color={item.status === GeneralStatusEnum.ACTIVE ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell>
                <RowActionsMenu
                  name={item.name}
                  actions={[{ label: t('billing.platform.remove'), danger: true, onClick: () => setToDelete(item) }]}
                />
              </TableCell>
            </TableRow>
          ))}
        </BasicTable>
      </Box>

      {creating && <PlatformItemModal mode={ActionMode.CREATE} onClose={() => setCreating(false)} onSaved={loadItems} />}
      {editing && <PlatformItemModal mode={ActionMode.EDIT} item={editing} onClose={() => setEditing(null)} onSaved={loadItems} />}
      {toDelete && (
        <GenericInfoModal
          open
          onClose={() => setToDelete(null)}
          onConfirm={handleDelete}
          label={t('billing.platform.remove')}
          message={t('billing.platform.removeConfirm')}
          confirmLabel={t('common.remove')}
          entityName={toDelete.name}
        />
      )}
    </Box>
  );
};

export default PlatformItemsTab;
